<script setup lang="ts">
import { computed, toRef } from "vue";
import { useTranslation } from "i18next-vue";
import {
  useGetDiagnosticReports,
  useCreateReport,
  useSetDefaultReport,
} from "@/stores/queries";
import { useNavigate } from "@/composable/useNavigate";
import type { Diagnostic, Report } from "@/types";
import DiagnosticReportItem from "./DiagnosticReportItem.vue";
import SectionPlaceholderAction from "./SectionPlaceholderAction.vue";

/** Rapports de l'ouvrage : le rapport actuel est mis en avant, les autres suivent. */

const props = defineProps<{
  diagnostic: Diagnostic;
}>();

const { t } = useTranslation();
const { navigateReport } = useNavigate();

const diagnosticId = computed(() => props.diagnostic.id);
const diagnosticIdRef = toRef(diagnosticId);

const { data: reports, isLoading } = useGetDiagnosticReports(diagnosticIdRef);
const { mutateAsync: createReport, isPending: isCreatingReport } =
  useCreateReport(diagnosticIdRef);
const { mutateAsync: setDefaultReport } = useSetDefaultReport(diagnosticIdRef);

const isDefaultReport = (report: Report) =>
  props.diagnostic.defaultReportId === report.id;

/**
 * Nom de repli d'un rapport sans titre : le nom de l'ouvrage suivi du n° de
 * version. « Rapport sans nom » ne distinguait pas deux rapports du même
 * ouvrage. La version se compte du plus ancien au plus récent, indépendamment
 * de l'ordre d'affichage.
 */
const versionByReportId = computed(() => {
  const map = new Map<number, number>();
  [...(reports.value ?? [])]
    .sort(
      (a, b) =>
        new Date(a.createdAt).getTime() - new Date(b.createdAt).getTime()
    )
    .forEach((report, index) => map.set(report.id, index + 1));
  return map;
});

const fallbackName = (report: Report): string => {
  const base =
    props.diagnostic.name?.trim() || t("report:diagnostics.untitledReport");
  return `${base} (v${versionByReportId.value.get(report.id) ?? 1})`;
};

/** Le rapport en vigueur passe en tête de liste. */
const orderedReports = computed(() => {
  const all = reports.value ?? [];
  return [
    ...all.filter((report) => isDefaultReport(report)),
    ...all.filter((report) => !isDefaultReport(report)),
  ];
});

const handleCreateReport = async () => {
  const report = await createReport(undefined);
  if (!props.diagnostic.defaultReportId) {
    await setDefaultReport(report.id);
  }
  navigateReport(report.id);
};
</script>

<template>
  <section class="diagnostic-block">
    <header class="block-header">
      <h3 class="block-title">
        {{ t("report:diagnostics.reportsTitle") }}
        <span v-if="reports?.length" class="count-pill">
          {{ reports.length }}
        </span>
      </h3>
      <p class="block-description">
        {{ t("report:diagnostics.reportsDescription") }}
      </p>
    </header>

    <div v-if="isLoading" class="text-center py-3">
      <span class="spinner-border spinner-border-sm" />
    </div>

    <template v-else>
      <!-- Le rapport en vigueur en tête, les versions précédentes estompées -->
      <div v-if="orderedReports.length" class="reports-list">
        <DiagnosticReportItem
          v-for="report in orderedReports"
          :key="report.id"
          :report="report"
          :fallback-name="fallbackName(report)"
          :current="isDefaultReport(report)"
          @open="navigateReport(report.id)"
          @set-default="setDefaultReport(report.id)"
        />
      </div>

      <SectionPlaceholderAction
        icon="bi-plus-lg"
        :label="
          t(
            orderedReports.length
              ? 'report:diagnostics.newBlankReport'
              : 'report:diagnostics.createFirstReport'
          )
        "
        :loading="isCreatingReport"
        @click="handleCreateReport"
      />
    </template>
  </section>
</template>

<style lang="scss" scoped>
.diagnostic-block {
  display: flex;
  flex-direction: column;
  gap: 12px;
}

.block-header {
  margin: 0;
}

.reports-list {
  display: flex;
  flex-direction: column;
  gap: 4px;
}

.block-title {
  display: flex;
  align-items: center;
  gap: 8px;
  font-size: 17px;
  font-weight: 600;
  margin: 0;
}

.count-pill {
  font-size: 12px;
  font-weight: 500;
  background-color: var(--bs-tertiary-bg);
  color: var(--bs-secondary-color);
  border-radius: 10px;
  padding: 1px 8px;
}

.block-description {
  font-size: 13px;
  color: var(--bs-secondary-color);
  margin: 4px 0 0;
}

</style>
